import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import type { DocumentReleaseDetails } from "~/api/types";
import {
  updateDocumentRelease,
  type UpdateDocumentReleaseData,
} from "~/api/v2/requests";
import { Button } from "~/atoms/button";
import { DialogFooter } from "~/atoms/dialog";
import { isSameDateValue, toDate, toISODateString } from "~/helpers/date";
import { canEditWorkflowStep } from "~/helpers/documentPermissions";
import { mapStepToWorkflowStepData } from "~/helpers/workflow";
import {
  useWorkflowDatesEditor,
  type WorkflowDates,
} from "~/hooks/useWorkflowDatesEditor";
import { DueDateGrid } from "~/organisms/DueDateGrid";

type Artifact = DocumentReleaseDetails["artifact"];

interface WorkflowScheduleEditFormProps {
  release: DocumentReleaseDetails;
  onCancel: () => void;
  onSaved: () => Promise<void>;
}

function mapArtifactToWorkflowDatesRow(artifact: Artifact) {
  return {
    id: artifact.id,
    title: artifact.title,
    reviewFrom: toDate(artifact.reviewFrom),
    reviewTo: toDate(artifact.reviewTo),
    stepDueDates: artifact.steps.map((step) => toDate(step.dueAt)),
    lockedFields: artifact.steps.flatMap((step, index) =>
      canEditWorkflowStep(step) ? [] : [index],
    ),
  };
}

// Only what changed, so an untouched workflow is not rewritten.
function mapWorkflowDatesToUpdateReleaseData(
  artifact: Artifact,
  dates: WorkflowDates,
): UpdateDocumentReleaseData {
  const data: UpdateDocumentReleaseData = {};

  if (!isSameDateValue(dates.reviewFrom, artifact.reviewFrom)) {
    data.reviewFrom = toISODateString(dates.reviewFrom);
  }
  if (!isSameDateValue(dates.reviewTo, artifact.reviewTo)) {
    data.reviewTo = toISODateString(dates.reviewTo);
  }

  const stepsChanged = artifact.steps.some(
    (step, index) => !isSameDateValue(dates.stepDueDates[index], step.dueAt),
  );
  if (stepsChanged) {
    data.steps = artifact.steps.map((step, index) =>
      mapStepToWorkflowStepData({
        ...step,
        dueAt: dates.stepDueDates[index] ?? null,
      }),
    );
  }

  return data;
}

export const WorkflowScheduleEditForm = ({
  release,
  onCancel,
  onSaved,
}: WorkflowScheduleEditFormProps) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { artifact } = release;
  const rows = [mapArtifactToWorkflowDatesRow(artifact)];
  const datesEditor = useWorkflowDatesEditor(rows, artifact.steps.length);

  const handleSave = async () => {
    const dates = datesEditor.dates.get(artifact.id);
    if (!dates) return;

    const payload = mapWorkflowDatesToUpdateReleaseData(artifact, dates);
    if (Object.keys(payload).length === 0) {
      onCancel();
      return;
    }

    try {
      setIsSubmitting(true);
      await updateDocumentRelease(release.id, payload);
      toast.success(t(($) => $.toasts.success.documentUpdated));
      await onSaved();
      onCancel();
    } catch {
      toast.error(t(($) => $.toasts.error.generic));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 pt-2">
      <DueDateGrid
        rows={rows}
        steps={artifact.steps}
        datesEditor={datesEditor}
      />
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          {t(($) => $.commons.buttons.cancel)}
        </Button>
        <Button
          type="button"
          onClick={handleSave}
          disabled={isSubmitting || datesEditor.hasErrors}
        >
          {t(($) => $.commons.buttons.save)}
        </Button>
      </DialogFooter>
    </div>
  );
};
